import { addDays, startOfDay } from 'date-fns';

export type HeatmapMetric = 'counter' | 'speed_value';

/** API response: keys are `@lat,lng|quarter_hour_iso`; `q` is the quarter hour, `v` a numeric string. */
export type GridApiResponse = Record<string, { v: string; q: string; h?: string }>;

export type HeatmapCell = { lat: number; lng: number; value: number };

/**
 * One metric, parsed once, rows sorted by time. Kept in typed arrays (and out of redux, see
 * `datasetCache`) so a month of quarter-hours costs a few MB instead of a frozen object tree.
 */
export type HeatmapDataset = {
  metric: HeatmapMetric;
  lat: Float64Array; // per cell
  lng: Float64Array; // per cell
  t: Float64Array; // per row, ascending
  cell: Uint32Array; // per row
  v: Float32Array; // per row; speed already in km/h
};

/** ETSI SpeedValue is 0.01 m/s; km/h = v × 0.036 (same as the CAM list). `v` is already a per-cell average. */
export const ETSI_SPEED_TO_KMH = 0.036;
export const etsiSpeedToKmh = (v: number): number => v * ETSI_SPEED_TO_KMH;

export const MS_QUARTER_HOUR = 15 * 60 * 1000;
export const MS_HOUR = 4 * MS_QUARTER_HOUR;
export const MS_DAY = 24 * MS_HOUR;

/** `@45.48,8.83|2026-08-20T15:00:00.000Z` → `[45.48, 8.83]`, null when malformed. */
export const parseGridKey = (key: string): [number, number] | null => {
  if (!key.startsWith('@')) return null;
  const pipe = key.indexOf('|');
  const parts = (pipe >= 0 ? key.slice(1, pipe) : key.slice(1)).split(',');
  if (parts.length !== 2) return null;
  const lat = parseFloat(parts[0]);
  const lng = parseFloat(parts[1]);
  return Number.isNaN(lat) || Number.isNaN(lng) ? null : [lat, lng];
};

export const parseGridResponse = (res: GridApiResponse, metric: HeatmapMetric): HeatmapDataset => {
  const cellIds = new Map<string, number>();
  const lat: number[] = [];
  const lng: number[] = [];
  const rows: Array<[number, number, number]> = []; // t, cell, v
  Object.entries(res ?? {}).forEach(([key, entry]) => {
    const coords = parseGridKey(key);
    if (!coords || !entry) return;
    const t = Date.parse(entry.q);
    const raw = parseFloat(entry.v);
    if (Number.isNaN(t) || Number.isNaN(raw)) return;
    const cellKey = `${coords[0]},${coords[1]}`;
    let id = cellIds.get(cellKey);
    if (id === undefined) {
      id = lat.length;
      cellIds.set(cellKey, id);
      lat.push(coords[0]);
      lng.push(coords[1]);
    }
    rows.push([t, id, metric === 'speed_value' ? etsiSpeedToKmh(raw) : raw]);
  });
  rows.sort((a, b) => a[0] - b[0]);
  return {
    metric,
    lat: Float64Array.from(lat),
    lng: Float64Array.from(lng),
    t: Float64Array.from(rows, (r) => r[0]),
    cell: Uint32Array.from(rows, (r) => r[1]),
    v: Float32Array.from(rows, (r) => r[2]),
  };
};

/** First row index with t >= x (rows are sorted). */
export const lowerBound = (t: Float64Array, x: number): number => {
  let lo = 0;
  let hi = t.length;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (t[mid] < x) lo = mid + 1;
    else hi = mid;
  }
  return lo;
};

/**
 * Per-cell value over the half-open window [start, end): counter = sum of vehicles,
 * speed = plain mean of the quarter-hour averages. Cells with no row in the window are omitted.
 */
export const aggregateWindow = (ds: HeatmapDataset, start: number, end: number): HeatmapCell[] => {
  const sum = new Float64Array(ds.lat.length);
  const n = new Uint32Array(ds.lat.length);
  for (let i = lowerBound(ds.t, start); i < ds.t.length && ds.t[i] < end; i += 1) {
    sum[ds.cell[i]] += ds.v[i];
    n[ds.cell[i]] += 1;
  }
  const out: HeatmapCell[] = [];
  for (let c = 0; c < n.length; c += 1) {
    const value = ds.metric === 'speed_value' ? sum[c] / n[c] : sum[c];
    // speed 0 means "no moving vehicle reported"; not worth a coloured square
    if (n[c] > 0 && value > 0) out.push({ lat: ds.lat[c], lng: ds.lng[c], value });
  }
  return out;
};

export const valueRange = (cells: HeatmapCell[]): { min: number; max: number } | null => {
  if (cells.length === 0) return null;
  let min = Infinity;
  let max = -Infinity;
  cells.forEach(({ value }) => {
    if (value < min) min = value;
    if (value > max) max = value;
  });
  return { min, max };
};

/**
 * Fixed colour scale: min/max of the per-cell value over *every* slot of `slotMs` in [start, end),
 * so colours stay comparable while stepping through slots. One pass over the rows.
 */
export const scaleAcrossSlots = (
  ds: HeatmapDataset,
  start: number,
  end: number,
  slotMs: number,
): { min: number; max: number } | null => {
  const sums = new Map<number, { sum: number; n: number }>();
  const cells = ds.lat.length;
  for (let i = lowerBound(ds.t, start); i < ds.t.length && ds.t[i] < end; i += 1) {
    const key = Math.floor((ds.t[i] - start) / slotMs) * cells + ds.cell[i];
    const acc = sums.get(key);
    if (acc) {
      acc.sum += ds.v[i];
      acc.n += 1;
    } else sums.set(key, { sum: ds.v[i], n: 1 });
  }
  const values: HeatmapCell[] = [];
  sums.forEach(({ sum, n }) => {
    const value = ds.metric === 'speed_value' ? sum / n : sum;
    if (value > 0) values.push({ lat: 0, lng: 0, value });
  });
  return valueRange(values);
};

export const GRANULARITIES = [MS_QUARTER_HOUR, MS_HOUR / 2, MS_HOUR, MS_DAY];

/** Client defaults for the chart/slot granularity: < 4 h → 15 min, up to 2 days → 1 h, longer → 1 day. */
export const defaultGranularity = (durationMs: number): number => {
  if (durationMs < 4 * MS_HOUR) return MS_QUARTER_HOUR;
  return durationMs <= 2 * MS_DAY ? MS_HOUR : MS_DAY;
};

/**
 * Whole-network series for the chart: per bucket, counter = vehicles (sum), speed = mean of the
 * quarter-hour cell averages. Buckets start at `start`, so the last one may be partial.
 */
export const bucketSeries = (
  ds: HeatmapDataset,
  start: number,
  end: number,
  bucketMs: number,
): { starts: number[]; values: number[] } => {
  const count = Math.max(0, Math.ceil((end - start) / bucketMs));
  const sums = new Float64Array(count);
  const ns = new Uint32Array(count);
  for (let i = lowerBound(ds.t, start); i < ds.t.length && ds.t[i] < end; i += 1) {
    const b = Math.floor((ds.t[i] - start) / bucketMs);
    sums[b] += ds.v[i];
    ns[b] += 1;
  }
  return {
    starts: Array.from({ length: count }, (_, b) => start + b * bucketMs),
    values: Array.from(sums, (s, b) => {
      if (ds.metric === 'counter') return s;
      return ns[b] > 0 ? s / ns[b] : 0;
    }),
  };
};

/**
 * Parsed datasets live here, not in redux: they are large, immutable and only read by the map and
 * the chart, which re-read them when `state.heatmap.dataKey` changes. Small LRU so toggling the
 * metric or range back and forth does not refetch.
 */
const CACHE_SIZE = 8; // slices are views over their parent's arrays, so entries are cheap
type CacheEntry = { ds: HeatmapDataset; start: number; end: number; fetchedAt: number };
const datasetCache = new Map<string, CacheEntry>();

export const heatmapKey = (metric: HeatmapMetric, start: number, end: number): string =>
  `${metric}|${start}|${end}`;

export const getDataset = (key: string | null): HeatmapDataset | null =>
  (key && datasetCache.get(key)?.ds) || null;

export const setDataset = (
  ds: HeatmapDataset,
  start: number,
  end: number,
  fetchedAt = Date.now(),
): string => {
  const key = heatmapKey(ds.metric, start, end);
  datasetCache.delete(key);
  datasetCache.set(key, { ds, start, end, fetchedAt });
  if (datasetCache.size > CACHE_SIZE) datasetCache.delete(datasetCache.keys().next().value);
  return key;
};

/** Rows of `ds` in [start, end), as views over the same arrays (no copy). */
export const sliceDataset = (ds: HeatmapDataset, start: number, end: number): HeatmapDataset => {
  const i = lowerBound(ds.t, start);
  const j = lowerBound(ds.t, end);
  return { ...ds, t: ds.t.subarray(i, j), cell: ds.cell.subarray(i, j), v: ds.v.subarray(i, j) };
};

/**
 * A cached interval of the same metric containing [start, end), already sliced; null if none.
 * It must have been fetched after `end`, so a range that reached into the present when cached
 * (and may have missed its latest quarter-hours) is never reused for later data.
 * ponytail: one covering interval only; merging adjacent cached intervals is not attempted.
 */
export const findCovering = (
  metric: HeatmapMetric,
  start: number,
  end: number,
): { ds: HeatmapDataset; fetchedAt: number } | null => {
  let found: { ds: HeatmapDataset; fetchedAt: number } | null = null;
  datasetCache.forEach((e) => {
    if (!found && e.ds.metric === metric && e.start <= start && e.end >= end && e.fetchedAt >= end) {
      found = { ds: sliceDataset(e.ds, start, end), fetchedAt: e.fetchedAt };
    }
  });
  return found;
};

export const DAY_GRANULARITIES = [MS_HOUR, MS_HOUR / 2, MS_QUARTER_HOUR];

export type DayWindow = {
  /** Local midnight of the calendar day; bucket 0 of the day series starts here */
  dayStart: number;
  /** Part of the day inside the interval, [start, end) */
  start: number;
  end: number;
};

/** Calendar days (local time, DST-aware) touched by [start, end); first/last are clipped to it. */
export const splitDays = (start: number, end: number): DayWindow[] => {
  const out: DayWindow[] = [];
  for (let d = startOfDay(new Date(start)); d.getTime() < end; d = addDays(d, 1)) {
    const dayStart = d.getTime();
    out.push({ dayStart, start: Math.max(start, dayStart), end: Math.min(end, addDays(d, 1).getTime()) });
  }
  return out;
};

/**
 * One value per time-of-day bucket of a day, network-wide: counter = vehicles, speed = mean of the
 * quarter-hour cell averages. Buckets outside the covered part of the day are null; a counter
 * bucket inside it with no rows is 0, a speed one null (no vehicle, no speed).
 * ponytail: buckets count from local midnight, so on the two DST days everything after the shift is
 * off by one hour and a 25 h day loses its last hour; fine for trend spotting.
 */
export const daySeries = (ds: HeatmapDataset, day: DayWindow, bucketMs: number): Array<number | null> => {
  const count = Math.floor(MS_DAY / bucketMs);
  const sums = new Float64Array(count);
  const ns = new Uint32Array(count);
  for (let i = lowerBound(ds.t, day.start); i < ds.t.length && ds.t[i] < day.end; i += 1) {
    const b = Math.floor((ds.t[i] - day.dayStart) / bucketMs);
    if (b < count) {
      sums[b] += ds.v[i];
      ns[b] += 1;
    }
  }
  return Array.from(sums, (sum, b) => {
    const bStart = day.dayStart + b * bucketMs;
    if (bStart + bucketMs <= day.start || bStart >= day.end) return null;
    if (ds.metric === 'counter') return sum;
    return ns[b] > 0 ? sum / ns[b] : null;
  });
};

/** Per-bucket mean and median over the series that have a value there (null when none does). */
export const dayStats = (
  series: Array<Array<number | null>>,
): { mean: Array<number | null>; median: Array<number | null> } => {
  const buckets = series.reduce((m, s) => Math.max(m, s.length), 0);
  const mean: Array<number | null> = [];
  const median: Array<number | null> = [];
  for (let b = 0; b < buckets; b += 1) {
    const vals = series
      .map((s) => s[b])
      .filter((v): v is number => v !== null && v !== undefined)
      .sort((x, y) => x - y);
    if (vals.length === 0) {
      mean.push(null);
      median.push(null);
    } else {
      const mid = Math.floor(vals.length / 2);
      mean.push(vals.reduce((a, v) => a + v, 0) / vals.length);
      median.push(vals.length % 2 ? vals[mid] : (vals[mid - 1] + vals[mid]) / 2);
    }
  }
  return { mean, median };
};

/** Max two decimals for anything a chart shows (averages and speeds are not integers). */
export const round2 = (v: number): number => Math.round(v * 100) / 100;

/**
 * Every (cell, quarter-hour) value in [start, end) as a time/value point, zero speeds dropped.
 * Over `max` points a fixed stride thins them out (same picture on every render); `total` is the count before.
 */
export const scatterPoints = (
  ds: HeatmapDataset,
  start: number,
  end: number,
  max = 20000,
): { points: Array<{ x: number; y: number }>; total: number } => {
  const idx: number[] = [];
  for (let i = lowerBound(ds.t, start); i < ds.t.length && ds.t[i] < end; i += 1) {
    if (ds.v[i] > 0) idx.push(i);
  }
  const stride = Math.max(1, Math.ceil(idx.length / max));
  const points: Array<{ x: number; y: number }> = [];
  for (let k = 0; k < idx.length; k += stride) points.push({ x: ds.t[idx[k]], y: ds.v[idx[k]] });
  return { points, total: idx.length };
};

/** API response of vehicle-counts-over-time: per granularity, UTC bucket start → distinct vehicles `v`. */
export type VehicleCountsResponse = Record<'1d' | '1h' | '30m' | '15m', Record<string, { v: string; n: string }>>;

/** Distinct vehicles per bucket start (epoch ms), one map per granularity in ms (not additive across them). */
export type VehicleCounts = Map<number, Map<number, number>>;

export const parseVehicleCounts = (res: VehicleCountsResponse): VehicleCounts => {
  const gran: Array<[number, keyof VehicleCountsResponse]> = [
    [MS_QUARTER_HOUR, '15m'],
    [MS_HOUR / 2, '30m'],
    [MS_HOUR, '1h'],
    [MS_DAY, '1d'],
  ];
  return new Map(
    gran.map(([ms, key]) => [
      ms,
      new Map(Object.entries(res?.[key] ?? {}).map(([iso, c]) => [Date.parse(iso), Number(c.v)])),
    ]),
  );
};

const vehicleCache = new Map<string, VehicleCounts>();
export const vehicleKey = (start: number, end: number): string => `${start}|${end}`;
export const getVehicleCounts = (key: string | null): VehicleCounts | null => (key && vehicleCache.get(key)) || null;
export const setVehicleCounts = (start: number, end: number, counts: VehicleCounts): string => {
  const key = vehicleKey(start, end);
  vehicleCache.delete(key);
  vehicleCache.set(key, counts);
  if (vehicleCache.size > 4) vehicleCache.delete(vehicleCache.keys().next().value);
  return key;
};

/** Counter chart series: bars sit on UTC multiples of `bucketMs` (the API's buckets); empty buckets are 0. */
export const vehicleSeries = (
  counts: VehicleCounts,
  start: number,
  end: number,
  bucketMs: number,
): { starts: number[]; values: number[] } => {
  const origin = Math.floor(start / bucketMs) * bucketMs;
  const n = Math.max(0, Math.ceil((end - origin) / bucketMs));
  const starts = Array.from({ length: n }, (_, b) => origin + b * bucketMs);
  const m = counts.get(bucketMs);
  return { starts, values: starts.map((t) => m?.get(t) ?? 0) };
};

/**
 * Counter version of `daySeries`: null outside the covered part of the day, 0 for an empty bucket inside it.
 * ponytail: looks buckets up from local midnight, exact only for UTC offsets that are multiples of `bucketMs`.
 */
export const vehicleDaySeries = (counts: VehicleCounts, day: DayWindow, bucketMs: number): Array<number | null> => {
  const m = counts.get(bucketMs);
  return Array.from({ length: Math.floor(MS_DAY / bucketMs) }, (_, b) => {
    const bStart = day.dayStart + b * bucketMs;
    if (bStart + bucketMs <= day.start || bStart >= day.end) return null;
    return m?.get(bStart) ?? 0;
  });
};
